import { API_ENDPOINTS } from '@tsgecart/config';
import { adminApiFetch } from '../../api/admin-client.js';
import type {
  AdminPaymentListItem,
  AdminPaymentDetail,
  AdminRefundListItem,
  PaymentListParams,
  RefundListParams,
  InitiateRefundInput,
  InitiateRefundResult,
} from '../types/payments.js';

export interface FetchOptions {
  token: string;
  signal?: AbortSignal;
}

function buildQuery(params: Record<string, string | number | boolean | string[] | undefined>): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined) continue;
    search.set(key, Array.isArray(value) ? value.join(',') : String(value));
  }
  const query = search.toString();
  return query ? `?${query}` : '';
}

export async function fetchAdminPayments(
  params: PaymentListParams,
  options: FetchOptions,
): Promise<AdminPaymentListItem[]> {
  const query = buildQuery({
    status: params.status,
    method: params.method,
    orderNumber: params.orderNumber,
    customerSearch: params.customerSearch,
    dateFrom: params.dateFrom,
    dateTo: params.dateTo,
    minAmountPaise: params.minAmountPaise,
    maxAmountPaise: params.maxAmountPaise,
    sortField: params.sortField,
    sortDirection: params.sortDirection,
    limit: params.limit,
    offset: params.offset,
  });
  return adminApiFetch<AdminPaymentListItem[]>(`${API_ENDPOINTS.adminPayments}${query}`, options);
}

export async function fetchAdminPaymentDetail(
  paymentId: string,
  options: FetchOptions,
): Promise<AdminPaymentDetail> {
  return adminApiFetch<AdminPaymentDetail>(`${API_ENDPOINTS.adminPayments}/${paymentId}`, options);
}

export async function initiateAdminRefund(
  paymentId: string,
  input: InitiateRefundInput,
  options: FetchOptions,
): Promise<InitiateRefundResult> {
  return adminApiFetch<InitiateRefundResult>(`${API_ENDPOINTS.adminPayments}/${paymentId}/refunds`, {
    ...options,
    method: 'POST',
    body: input,
  });
}

export async function fetchAdminRefunds(
  params: RefundListParams,
  options: FetchOptions,
): Promise<AdminRefundListItem[]> {
  const query = buildQuery({
    status: params.status,
    orderNumber: params.orderNumber,
    dateFrom: params.dateFrom,
    dateTo: params.dateTo,
    minAmountPaise: params.minAmountPaise,
    maxAmountPaise: params.maxAmountPaise,
    limit: params.limit,
    offset: params.offset,
  });
  return adminApiFetch<AdminRefundListItem[]>(`${API_ENDPOINTS.adminRefunds}${query}`, options);
}

export async function fetchAdminRefundDetail(
  refundId: string,
  options: FetchOptions,
): Promise<AdminRefundListItem> {
  return adminApiFetch<AdminRefundListItem>(`${API_ENDPOINTS.adminRefunds}/${refundId}`, options);
}
